import type { ComputedRef, Ref } from 'vue'
import { emitter } from '@/plugins/emitter'
import type { StartAIStreamOptions } from '@/composables/yagiprime/useAiStreaming'

type FormatData = Record<string, unknown> & {
  reason?: string
  lastestEdu?: string
  lastestJob?: string
  ai_strength?: string
  ai_company_url?: string
  ai_company_name?: string
  ai_company_values?: string
  hopeIndustrycategory?: string
  hopeJobcategory?: string
  currentIndustrycategory?: string
  currentJobcategory?: string
  licenseHistories?: Array<{ description?: string }>
}

type AxiosLike = {
  post: (
    url: string,
    data: unknown,
    config?: { timeout?: number }
  ) => Promise<{ data: Record<string, unknown> }>
}

type GenerateDeps = {
  formatData: FormatData
  isAuthenticated: ComputedRef<boolean>
  user: Ref<{ uid: string } | null>
  axios: AxiosLike
  varuna: { endPoints: Record<string, string> }
  globalVar: { isYagiPrime: boolean }
  message: (opts: Record<string, unknown>) => unknown
  vm: unknown
  yagiprimePaymentService: {
    useFeature: (
      vm: unknown,
      feature: string,
      hooks: { onSuccess: () => void; onCancel: () => void }
    ) => void
  }
  aiResultView: Ref<boolean>
  aiResultMyprResult: Ref<string>
  aiIsStreaming: Ref<boolean>
  aiUsageCount: Ref<number>
  isAutoExecuteAfterPayment: Ref<boolean>
  isExtractingCompanyInfo: Ref<boolean>
  isCompanyInfoExtracted: Ref<boolean>
  aiMyprVisible: Ref<boolean>
  startAIStream: (
    endpointUrl: string,
    formData: FormData,
    options?: StartAIStreamOptions
  ) => void
  openAiMyprModal: () => void
  closeAiMyprDialog: () => void
  updateFormatField: (key: string, value: unknown) => void
  updateReason: (text: string) => void
  getAiUsageCount: () => void
}

export function createReasonAiMyprGenerate(deps: GenerateDeps) {
  function logAiApply() {
    if (!deps.isAuthenticated.value || !deps.user.value) return
    const body = new FormData()
    body.append('uuid', deps.user.value.uid)
    body.append('category_type', 'reason')
    deps.axios.post(deps.varuna.endPoints.logAiApply, body).catch(() => {})
  }

  function extractCompanyInfo() {
    const url = deps.formatData.ai_company_url as string
    if (!url) {
      deps.message({ message: 'URLを入力してください', type: 'warning' })
      return
    }
    deps.isExtractingCompanyInfo.value = true
    deps.axios
      .post(deps.varuna.endPoints.aiExtractCompanyInfo, { url })
      .then((response) => {
        const data = response.data as {
          success?: boolean
          data?: {
            company_name?: string
            industry?: string
            job?: string
            values?: string
          }
        }
        if (data.success && data.data) {
          const info = data.data
          if (info.company_name) {
            deps.updateFormatField('ai_company_name', info.company_name)
          }
          if (info.industry) {
            deps.updateFormatField('hopeIndustrycategory', info.industry)
          }
          if (info.job) deps.updateFormatField('hopeJobcategory', info.job)
          if (info.values) {
            deps.updateFormatField('ai_company_values', info.values)
          }
          deps.isCompanyInfoExtracted.value = true
          deps.message({ message: '企業情報を取得しました', type: 'success' })
        } else {
          deps.message({
            message: '企業情報の取得に失敗しました',
            type: 'error'
          })
        }
      })
      .catch(() => {
        deps.message({
          message: '企業情報の取得に失敗しました',
          type: 'error'
        })
      })
      .finally(() => {
        deps.isExtractingCompanyInfo.value = false
      })
  }

  function getAIMypr(opts: { showModal?: boolean } = {}) {
    if (!deps.isAuthenticated.value || !deps.user.value) return
    const showModal = opts.showModal !== false
    const body = new FormData()
    const fd = deps.formatData
    body.append('uuid', deps.user.value.uid)
    body.append('education', (fd.lastestEdu as string) || '')
    body.append('job', (fd.lastestJob as string) || '')
    body.append(
      'licenses',
      (fd.licenseHistories &&
        fd.licenseHistories.map((l) => l.description).join(',')) ||
        ''
    )
    body.append('strength', (fd.ai_strength as string) || '')
    body.append('company_name', (fd.ai_company_name as string) || '')
    body.append('company_industry', (fd.hopeIndustrycategory as string) || '')
    body.append('company_job', (fd.hopeJobcategory as string) || '')
    body.append('company_url', (fd.ai_company_url as string) || '')
    body.append('company_values', (fd.ai_company_values as string) || '')
    body.append(
      'current_industry',
      (fd.currentIndustrycategory as string) || ''
    )
    body.append('current_job', (fd.currentJobcategory as string) || '')
    body.append('reason', (fd.reason as string) || '')

    if (showModal && !deps.aiMyprVisible.value) {
      deps.openAiMyprModal()
    }

    deps.startAIStream(deps.varuna.endPoints.aiIntroduceReason, body, {
      resultRef: deps.aiResultMyprResult,
      viewRef: deps.aiResultView,
      onDone: () => {
        deps.isAutoExecuteAfterPayment.value = false
        deps.getAiUsageCount()
      },
      onError: (error) => {
        const errMessage = error.message || '失敗しました'
        if (errMessage === 'AIの使用回数が上限に達しました。') {
          emitter.emit('show-yagiprime-payment-dialog', 'self_introduction')
        }
        deps.isAutoExecuteAfterPayment.value = false
      }
    })
  }

  function handleAIMypr() {
    const shouldShowModal = !deps.aiMyprVisible.value
    if (!deps.globalVar.isYagiPrime && deps.aiUsageCount.value < 1) {
      deps.aiUsageCount.value = 1
      deps.isAutoExecuteAfterPayment.value = true
      getAIMypr({ showModal: shouldShowModal })
      return
    }
    if (deps.globalVar.isYagiPrime) {
      getAIMypr({ showModal: shouldShowModal })
      return
    }
    const wasDialogVisible = deps.aiMyprVisible.value
    if (wasDialogVisible) deps.closeAiMyprDialog()
    deps.yagiprimePaymentService.useFeature(
      deps.vm,
      'self-introduction-shokumu',
      {
        onSuccess: () => {
          deps.isAutoExecuteAfterPayment.value = true
          getAIMypr()
        },
        onCancel: () => {
          if (!wasDialogVisible) return
          setTimeout(() => deps.openAiMyprModal(), 300)
        }
      }
    )
  }

  function getAIMyprContinue() {
    const body = new FormData()
    if (deps.isAuthenticated.value && deps.user.value) {
      body.append('uuid', deps.user.value.uid)
    }
    const fd = deps.formatData
    body.append(
      'data',
      '作成文章:短めの志望動機\n' +
        '学歴:' +
        ((fd.lastestEdu as string) || '') +
        '\n' +
        '職歴:' +
        ((fd.lastestJob as string) || '') +
        '\n' +
        '資格:' +
        ((fd.licenseHistories &&
          fd.licenseHistories.map((l) => l.description).join(',')) ||
          '') +
        '\n' +
        '得意なこと:' +
        ((fd.ai_strength as string) || '') +
        '\n' +
        '希望企業名:' +
        ((fd.ai_company_name as string) || '') +
        '\n' +
        '希望業界:' +
        ((fd.hopeIndustrycategory as string) || '') +
        '\n' +
        '希望職種:' +
        ((fd.hopeJobcategory as string) || '') +
        '\n' +
        '現在の業界:' +
        ((fd.currentIndustrycategory as string) || '') +
        '\n' +
        '現在の職種:' +
        ((fd.currentJobcategory as string) || '') +
        '\n' +
        '志望動機:' +
        ((fd.reason as string) || '') +
        '文章:' +
        deps.aiResultMyprResult.value.replace(/<br>/gi, '\n')
    )
    deps.startAIStream(deps.varuna.endPoints.aiSentenseContinue, body, {
      resultRef: deps.aiResultMyprResult,
      viewRef: deps.aiResultView,
      onError: (error) => {
        const errMessage = error.message || '失敗しました'
        if (errMessage === 'AIの使用回数が上限に達しました。') {
          emitter.emit('show-yagiprime-payment-dialog', 'self_introduction')
        }
      }
    })
  }

  function goYagiprimeRegister() {
    deps.yagiprimePaymentService.useFeature(deps.vm, 'self_introduction', {
      onSuccess: () => getAIMyprContinue(),
      onCancel: () => {
        deps.closeAiMyprDialog()
        deps.aiResultView.value = false
      }
    })
  }

  function applyAiMyprResult(closeDialog: boolean) {
    if (deps.aiIsStreaming.value) return
    deps.updateReason(deps.aiResultMyprResult.value.replace(/<br>/gi, '\n'))
    if (closeDialog) deps.closeAiMyprDialog()
    deps.aiResultView.value = false
    logAiApply()
  }

  return {
    extractCompanyInfo,
    handleAIMypr,
    goYagiprimeRegister,
    applyAiMyprResult
  }
}
